import { useDeferredValue, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { useTranslation } from 'react-i18next';

import ClearRoundedIcon from '@mui/icons-material/ClearRounded';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import PhoneRoundedIcon from '@mui/icons-material/PhoneRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import {
  Badge,
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  InputAdornment,
  MenuItem,
  Paper,
  Tab,
  Select,
  Stack,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Link as RouterLink, useLocation, useSearchParams } from 'react-router-dom';

import { EmptyState, ErrorState, LoadingState, PageContainer } from '@/shared/ui';

import { useBonusAccount, useCustomerCards, useCustomerTier } from '@/features/loyalty/hooks';
import { useLoyaltyPermissions } from '@/features/loyalty/lib/useLoyaltyPermissions';
import { CustomerConversationSummary, useCustomerConversation } from '@/features/conversations';

import { getIntlLocale } from '@/shared/i18n';
import { usePermissions } from '@/shared/permissions';

import {
  useCustomerAccounts,
  useCustomerAddresses,
  useCustomerConsents,
  useCustomerContacts,
  useCustomerRecipients,
  useCustomerSearch,
  useCustomerSummary,
} from '../../hooks';
import type { CustomerSearchRequest, CustomerSummary } from '../../model/types';

type StatusFilter =
  | 'all'
  | 'ACTIVE'
  | 'REGISTRATION_REQUIRED'
  | 'BLOCKED'
  | 'DELETED'
  | 'ANONYMIZED';
type ProfileFilter = 'all' | 'COMPLETE' | 'PHONE_REQUIRED';
type CustomerDetailTab =
  | 'overview'
  | 'contacts'
  | 'addresses'
  | 'consents'
  | 'recipients'
  | 'accounts'
  | 'loyalty'
  | 'conversation';

function formatDateTime(value?: string | null): string {
  if (!value) {
    return '—';
  }
  // Constructed fresh (not hoisted) so it follows the active i18next language on every call.
  return new Intl.DateTimeFormat(getIntlLocale(), {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
}

function SectionEmpty({ title, description }: { title: string; description: string }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
      <Stack spacing={0.75}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>
      </Stack>
    </Paper>
  );
}

function ChipRow({ children }: { children: ReactNode }) {
  return (
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
      {children}
    </Stack>
  );
}

function detailLabel(value?: string | null): string {
  return value && value.trim().length > 0 ? value : '—';
}

/** Joins name parts; returns '' (not a translated fallback) so the caller can apply one via `t()`. */
function fullName(firstName?: string | null, lastName?: string | null): string {
  return [firstName, lastName].filter((part) => part && part.trim().length > 0).join(' ');
}

function CustomerRecordCard({
  title,
  subtitle,
  chips,
  children,
}: {
  title: string;
  subtitle?: string;
  chips?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={1.25}>
        <Stack spacing={0.25}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {title}
          </Typography>
          {subtitle ? (
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          ) : null}
        </Stack>
        {chips ? <ChipRow>{chips}</ChipRow> : null}
        {children}
      </Stack>
    </Paper>
  );
}

function CustomerRow({
  customer,
  selected,
  onSelect,
}: {
  customer: CustomerSummary;
  selected: boolean;
  onSelect: (customerId: string) => void;
}) {
  return (
    <TableRow
      hover
      selected={selected}
      sx={{ cursor: 'pointer' }}
      onClick={() => onSelect(customer.customerId)}
    >
      <TableCell sx={{ width: '36%' }}>
        <Stack spacing={0.25}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {customer.displayName ?? customer.firstName ?? customer.customerId}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
            {customer.customerId}
          </Typography>
        </Stack>
      </TableCell>
      <TableCell sx={{ width: '24%' }}>
        <Stack spacing={0.5}>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <PhoneRoundedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="body2">{customer.primaryPhone ?? '—'}</Typography>
          </Stack>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <EmailRoundedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="body2">{customer.primaryEmail ?? '—'}</Typography>
          </Stack>
        </Stack>
      </TableCell>
      <TableCell sx={{ width: '14%' }}>
        <Stack spacing={0.75}>
          <Chip size="small" label={customer.status ?? '—'} />
          <Chip size="small" variant="outlined" label={customer.profileStatus ?? '—'} />
        </Stack>
      </TableCell>
      <TableCell sx={{ width: '12%' }}>
        <Chip size="small" variant="outlined" label={customer.registrationChannel ?? '—'} />
      </TableCell>
      <TableCell sx={{ width: '14%' }}>
        <Typography variant="body2" color="text.secondary">
          {formatDateTime(customer.updatedAt)}
        </Typography>
      </TableCell>
    </TableRow>
  );
}

function CustomerDetailPanel({
  customer,
  canManageLoyalty,
}: {
  customer: CustomerSummary;
  canManageLoyalty: boolean;
}) {
  const { t } = useTranslation('customers');
  const { hasPermission } = usePermissions();
  const [tab, setTab] = useState<CustomerDetailTab>('overview');
  const summary = useCustomerSummary(customer.customerId);
  const contacts = useCustomerContacts(customer.customerId);
  const addresses = useCustomerAddresses(customer.customerId);
  const recipients = useCustomerRecipients(customer.customerId);
  const consents = useCustomerConsents(customer.customerId);
  const accounts = useCustomerAccounts(customer.customerId);
  const loyaltyPermissions = useLoyaltyPermissions();
  const canViewConversations = hasPermission('conversations.view');
  const canManageConversations = canViewConversations && hasPermission('conversations.manage');
  const canReplyConversations =
    canManageConversations && hasPermission('conversations.feature.operator-reply');
  const canViewPii = hasPermission('customers.feature.pii-view');
  // Shares its cache entry with `CustomerConversationSummary`'s own fetch (same query key) — this
  // extra subscription only exists to know whether the workspace tab's label needs its "waiting" dot.
  const conversation = useCustomerConversation(customer.customerId, {
    enabled: canViewConversations,
  });
  const bonus = useBonusAccount(customer.customerId, { enabled: loyaltyPermissions.canView });
  const tier = useCustomerTier(customer.customerId, { enabled: loyaltyPermissions.canView });
  const cards = useCustomerCards(customer.customerId, { enabled: loyaltyPermissions.canView });

  useEffect(() => {
    setTab('overview');
  }, [customer.customerId]);

  const selected = summary.data ?? customer;
  const contactList = contacts.data ?? [];
  const addressList = addresses.data ?? [];
  const recipientList = recipients.data ?? [];
  const consentList = consents.data ?? [];
  const accountList = accounts.data ?? [];

  const loyaltyTab = (
    <Stack spacing={1.5}>
      <CustomerRecordCard
        title={t('workspace.loyalty.snapshotTitle')}
        subtitle={t('workspace.loyalty.snapshotSubtitle')}
        chips={
          <>
            {bonus.data ? (
              <Chip
                label={t('workspace.loyalty.points', { count: bonus.data.balance })}
                color="primary"
              />
            ) : null}
            {bonus.data ? <Chip label={bonus.data.status} variant="outlined" /> : null}
            {tier.data ? <Chip label={tier.data.name} variant="outlined" /> : null}
            <Chip
              label={t('workspace.loyalty.cards', { count: cards.data?.length ?? 0 })}
              variant="outlined"
            />
          </>
        }
      >
        {loyaltyPermissions.canView ? null : (
          <Typography variant="body2" color="text.secondary">
            {t('workspace.loyalty.hiddenForRole')}
          </Typography>
        )}
      </CustomerRecordCard>
      {canManageLoyalty ? (
        <Button
          variant="outlined"
          component={RouterLink}
          to={`/loyalty/customers?customerId=${customer.customerId}`}
        >
          {t('workspace.loyalty.openWorkspace')}
        </Button>
      ) : null}
    </Stack>
  );

  const content = (() => {
    switch (tab) {
      case 'contacts':
        return contacts.isError ? (
          <ErrorState
            title={t('workspace.tabs.contacts.loadError')}
            message={contacts.error?.message ?? t('common:error.unknown')}
          />
        ) : contacts.isLoading ? (
          <LoadingState label={t('workspace.tabs.contacts.loading')} />
        ) : contactList.length ? (
          <Stack spacing={1.5}>
            {contactList.map((contact) => (
              <CustomerRecordCard
                key={contact.id}
                title={detailLabel(contact.value)}
                subtitle={contact.contactTypeCode ?? t('workspace.tabs.contacts.fallbackType')}
                chips={
                  <>
                    <Chip
                      size="small"
                      label={
                        contact.verificationStatus ?? t('workspace.tabs.contacts.unknownStatus')
                      }
                    />
                    <Chip
                      size="small"
                      variant="outlined"
                      label={
                        contact.primary
                          ? t('workspace.tabs.contacts.primary')
                          : t('workspace.tabs.contacts.secondary')
                      }
                    />
                    <Chip
                      size="small"
                      variant="outlined"
                      label={
                        contact.active
                          ? t('workspace.status.active')
                          : t('workspace.status.inactive')
                      }
                    />
                  </>
                }
              >
                <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                  {t('workspace.tabs.contacts.normalized', {
                    value: detailLabel(contact.normalizedValue),
                  })}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('workspace.tabs.contacts.verified', {
                    date: formatDateTime(contact.verifiedAt),
                  })}
                </Typography>
              </CustomerRecordCard>
            ))}
          </Stack>
        ) : (
          <SectionEmpty
            title={t('workspace.tabs.contacts.emptyTitle')}
            description={t('workspace.tabs.contacts.emptyDescription')}
          />
        );
      case 'addresses':
        return addresses.isError ? (
          <ErrorState
            title={t('workspace.tabs.addresses.loadError')}
            message={addresses.error?.message ?? t('common:error.unknown')}
          />
        ) : addresses.isLoading ? (
          <LoadingState label={t('workspace.tabs.addresses.loading')} />
        ) : addressList.length ? (
          <Stack spacing={1.5}>
            {addressList.map((address) => (
              <CustomerRecordCard
                key={address.id}
                title={detailLabel(address.label ?? address.fullAddress)}
                subtitle={address.fullAddress ?? t('workspace.tabs.addresses.fallbackLabel')}
                chips={
                  <>
                    <Chip
                      size="small"
                      label={
                        address.active
                          ? t('workspace.status.active')
                          : t('workspace.status.inactive')
                      }
                    />
                    {address.recipientName ? (
                      <Chip size="small" variant="outlined" label={address.recipientName} />
                    ) : null}
                  </>
                }
              >
                <Typography variant="body2" color="text.secondary">
                  {[
                    address.city,
                    address.street,
                    address.house,
                    address.apartment
                      ? t('workspace.tabs.addresses.apartment', { apartment: address.apartment })
                      : null,
                  ]
                    .filter(Boolean)
                    .join(', ') || '—'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('workspace.tabs.addresses.recipientPhone', {
                    value: detailLabel(address.recipientPhone),
                  })}
                </Typography>
              </CustomerRecordCard>
            ))}
          </Stack>
        ) : (
          <SectionEmpty
            title={t('workspace.tabs.addresses.emptyTitle')}
            description={t('workspace.tabs.addresses.emptyDescription')}
          />
        );
      case 'consents':
        return consents.isError ? (
          <ErrorState
            title={t('workspace.tabs.consents.loadError')}
            message={consents.error?.message ?? t('common:error.unknown')}
          />
        ) : consents.isLoading ? (
          <LoadingState label={t('workspace.tabs.consents.loading')} />
        ) : consentList.length ? (
          <Stack spacing={1.5}>
            {consentList.map((consent) => (
              <CustomerRecordCard
                key={consent.id}
                title={consent.usage ?? t('workspace.tabs.consents.fallbackTitle')}
                subtitle={consent.touchpoint ?? t('workspace.tabs.consents.fallbackSubtitle')}
                chips={
                  <>
                    <Chip size="small" label={consent.value ?? '—'} />
                    {consent.legalBasis ? (
                      <Chip size="small" variant="outlined" label={consent.legalBasis} />
                    ) : null}
                  </>
                }
              >
                <Typography variant="body2" color="text.secondary">
                  {t('workspace.tabs.consents.captured', {
                    value: detailLabel(consent.captureSource),
                  })}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('workspace.tabs.consents.date', { date: formatDateTime(consent.captureDate) })}
                </Typography>
              </CustomerRecordCard>
            ))}
          </Stack>
        ) : (
          <SectionEmpty
            title={t('workspace.tabs.consents.emptyTitle')}
            description={t('workspace.tabs.consents.emptyDescription')}
          />
        );
      case 'recipients':
        return recipients.isError ? (
          <ErrorState
            title={t('workspace.tabs.recipients.loadError')}
            message={recipients.error?.message ?? t('common:error.unknown')}
          />
        ) : recipients.isLoading ? (
          <LoadingState label={t('workspace.tabs.recipients.loading')} />
        ) : recipientList.length ? (
          <Stack spacing={1.5}>
            {recipientList.map((recipient) => (
              <CustomerRecordCard
                key={recipient.id}
                title={
                  fullName(recipient.firstName, recipient.lastName) ||
                  t('workspace.fallbackRecipient')
                }
                subtitle={recipient.phone ?? recipient.email ?? t('workspace.fallbackRecipient')}
                chips={
                  <>
                    <Chip
                      size="small"
                      label={
                        recipient.isDefault
                          ? t('workspace.tabs.recipients.default')
                          : t('workspace.tabs.recipients.shared')
                      }
                    />
                    {recipient.isFavorite ? (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={t('workspace.tabs.recipients.favorite')}
                      />
                    ) : null}
                    {recipient.isDeleted ? (
                      <Chip size="small" color="warning" label={t('workspace.status.deleted')} />
                    ) : null}
                  </>
                }
              >
                <Typography variant="body2" color="text.secondary">
                  {t('workspace.tabs.recipients.email', { value: detailLabel(recipient.email) })}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('workspace.tabs.recipients.telegram', {
                    value: detailLabel(recipient.telegramUsername),
                  })}
                </Typography>
                {recipient.comment ? (
                  <Typography variant="body2" color="text.secondary">
                    {t('workspace.tabs.recipients.comment', { value: recipient.comment })}
                  </Typography>
                ) : null}
              </CustomerRecordCard>
            ))}
          </Stack>
        ) : (
          <SectionEmpty
            title={t('workspace.tabs.recipients.emptyTitle')}
            description={t('workspace.tabs.recipients.emptyDescription')}
          />
        );
      case 'accounts':
        return accounts.isError ? (
          <ErrorState
            title={t('workspace.tabs.accounts.loadError')}
            message={accounts.error?.message ?? t('common:error.unknown')}
          />
        ) : accounts.isLoading ? (
          <LoadingState label={t('workspace.tabs.accounts.loading')} />
        ) : accountList.length ? (
          <Stack spacing={1.5}>
            {accountList.map((account) => (
              <CustomerRecordCard
                key={`${account.provider}-${account.providerUserId ?? account.username ?? account.provider}`}
                title={account.provider}
                subtitle={
                  account.username ??
                  account.providerUserId ??
                  t('workspace.tabs.accounts.fallbackLabel')
                }
                chips={
                  <>
                    <Chip
                      size="small"
                      label={
                        account.active
                          ? t('workspace.status.active')
                          : t('workspace.status.inactive')
                      }
                    />
                    {account.channel ? (
                      <Chip size="small" variant="outlined" label={account.channel} />
                    ) : null}
                  </>
                }
              >
                <Typography variant="body2" color="text.secondary">
                  {t('workspace.tabs.accounts.providerUserId', {
                    value: detailLabel(account.providerUserId),
                  })}
                </Typography>
              </CustomerRecordCard>
            ))}
          </Stack>
        ) : (
          <SectionEmpty
            title={t('workspace.tabs.accounts.emptyTitle')}
            description={t('workspace.tabs.accounts.emptyDescription')}
          />
        );
      case 'loyalty':
        return loyaltyTab;
      case 'conversation':
        return canViewConversations ? (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <CustomerConversationSummary
              customerId={customer.customerId}
              messageLimit={20}
              canViewPii={canViewPii}
              canOpenInbox={canViewConversations}
              canReply={canReplyConversations}
            />
          </Paper>
        ) : null;
      case 'overview':
      default:
        return (
          <Stack spacing={1.5}>
            <CustomerRecordCard
              title={
                selected.displayName ?? selected.firstName ?? t('workspace.overview.fallbackName')
              }
              subtitle={selected.customerId}
              chips={
                <>
                  <Chip size="small" label={selected.status ?? t('workspace.status.unknown')} />
                  <Chip size="small" variant="outlined" label={selected.profileStatus ?? '—'} />
                  {selected.deleted ? (
                    <Chip size="small" color="warning" label={t('workspace.status.deleted')} />
                  ) : null}
                </>
              }
            >
              <Stack spacing={1}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <PhoneRoundedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                  <Typography variant="body2">
                    {selected.primaryPhone ?? t('workspace.overview.noPrimaryPhone')}
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <EmailRoundedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                  <Typography variant="body2">
                    {selected.primaryEmail ?? t('workspace.overview.noPrimaryEmail')}
                  </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  {t('workspace.overview.updated', { date: formatDateTime(selected.updatedAt) })}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('workspace.overview.registrationChannel', {
                    value: selected.registrationChannel ?? '—',
                  })}
                </Typography>
              </Stack>
            </CustomerRecordCard>

            {loyaltyPermissions.canView ? loyaltyTab : null}
          </Stack>
        );
    }
  })();

  return (
    <Stack spacing={2.25}>
      <Paper variant="outlined" sx={{ p: 2.5 }}>
        <Stack spacing={1.5}>
          <Box>
            <Typography variant="overline" color="text.secondary">
              {t('workspace.selectedCustomerLabel')}
            </Typography>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 800 }}>
              {selected.displayName ?? selected.firstName ?? t('workspace.overview.fallbackName')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
              {selected.customerId}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip size="small" label={selected.status ?? t('workspace.status.unknown')} />
            <Chip size="small" variant="outlined" label={selected.profileStatus ?? '—'} />
            {selected.deleted ? (
              <Chip size="small" color="warning" label={t('workspace.status.deleted')} />
            ) : null}
          </Stack>
          <Tabs
            value={tab}
            onChange={(_, next: CustomerDetailTab) => setTab(next)}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab value="overview" label={t('workspace.tabs.overview.label')} />
            <Tab
              value="contacts"
              label={t('workspace.tabs.contacts.tabLabel', { count: contactList.length })}
            />
            <Tab
              value="addresses"
              label={t('workspace.tabs.addresses.tabLabel', { count: addressList.length })}
            />
            <Tab
              value="consents"
              label={t('workspace.tabs.consents.tabLabel', { count: consentList.length })}
            />
            <Tab
              value="recipients"
              label={t('workspace.tabs.recipients.tabLabel', { count: recipientList.length })}
            />
            <Tab
              value="accounts"
              label={t('workspace.tabs.accounts.tabLabel', { count: accountList.length })}
            />
            <Tab value="loyalty" label={t('workspace.loyalty.tabLabel')} />
            {canViewConversations ? (
              <Tab
                value="conversation"
                label={
                  <Badge
                    variant="dot"
                    color="warning"
                    invisible={!conversation.data?.openEscalation}
                  >
                    {t('workspace.tabs.conversation.label')}
                  </Badge>
                }
                // The dot alone conveys nothing to a screen reader — combine the visible label with
                // the hand-off fact rather than letting a nested aria-label silently replace it
                // (the same MUI gotcha as a ToggleButton's aria-label overriding its visible text).
                aria-label={
                  conversation.data?.openEscalation
                    ? `${t('workspace.tabs.conversation.label')} — ${t('detail.sections.conversation.handoffWaiting')}`
                    : undefined
                }
              />
            ) : null}
          </Tabs>
        </Stack>
      </Paper>

      {content}
    </Stack>
  );
}

export function CustomerWorkspacePage() {
  const { t } = useTranslation('customers');
  const { hasPermission } = usePermissions();
  const { pathname } = useLocation();
  const [params, setParams] = useSearchParams();
  const [searchInput, setSearchInput] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [profileFilter, setProfileFilter] = useState<ProfileFilter>('all');
  const deferredSearch = useDeferredValue(searchInput);

  const canView = hasPermission('customers.view');
  const canManage = hasPermission('customers.manage');
  const canManageLoyalty = hasPermission('loyalty.manage');

  const request = useMemo<CustomerSearchRequest>(() => {
    const criteria: CustomerSearchRequest['criteria'] = {};
    if (statusFilter !== 'all') {
      if (statusFilter === 'DELETED') {
        criteria.deleted = true;
      } else {
        criteria.statuses = [statusFilter];
      }
    }
    if (profileFilter !== 'all') {
      criteria.profileStatuses = [profileFilter];
    }

    return {
      query: deferredSearch.trim() || undefined,
      criteria: Object.keys(criteria).length ? criteria : undefined,
      pagination: { page: 0, size: 20 },
      sort: [{ property: 'updatedAt', direction: 'DESC' }],
    };
  }, [deferredSearch, profileFilter, statusFilter]);

  const search = useCustomerSearch(request, { enabled: canView });

  const selectedCustomerId = params.get('customerId');
  const selectedCustomer =
    search.data?.content.find((customer) => customer.customerId === selectedCustomerId) ??
    search.data?.content[0] ??
    null;

  useEffect(() => {
    if (!search.data?.content.length) {
      return;
    }
    const nextSelected =
      search.data.content.find((customer) => customer.customerId === selectedCustomerId) ??
      search.data.content[0];
    if (nextSelected && nextSelected.customerId !== selectedCustomerId) {
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.set('customerId', nextSelected.customerId);
          return next;
        },
        { replace: true },
      );
    }
  }, [search.data?.content, selectedCustomerId, setParams]);

  const onSelectCustomer = (customerId: string) => {
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.set('customerId', customerId);
        return next;
      },
      { replace: true },
    );
  };

  const onReset = () => {
    setSearchInput('');
    setStatusFilter('all');
    setProfileFilter('all');
  };

  if (!canView) {
    return (
      <PageContainer title={t('workspace.title.default')}>
        <ErrorState
          title={t('workspace.accessRestricted.title')}
          message={t('workspace.accessRestricted.message')}
        />
      </PageContainer>
    );
  }

  const title = pathname.startsWith('/loyalty')
    ? t('workspace.title.loyalty')
    : t('workspace.title.default');

  return (
    <PageContainer title={title} subtitle={t('workspace.subtitle')}>
      <Stack spacing={2.5}>
        <Paper variant="outlined" sx={{ p: 2.25, position: 'sticky', top: 16, zIndex: 1 }}>
          <Stack spacing={2}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1.5}
              alignItems={{ md: 'center' }}
            >
              <TextField
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder={t('workspace.searchPlaceholder')}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRoundedIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>{t('workspace.statusLabel')}</InputLabel>
                <Select
                  value={statusFilter}
                  label={t('workspace.statusLabel')}
                  onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
                >
                  <MenuItem value="all">{t('workspace.statusOptions.all')}</MenuItem>
                  <MenuItem value="ACTIVE">{t('workspace.statusOptions.active')}</MenuItem>
                  <MenuItem value="REGISTRATION_REQUIRED">
                    {t('workspace.statusOptions.registrationRequired')}
                  </MenuItem>
                  <MenuItem value="BLOCKED">{t('workspace.statusOptions.blocked')}</MenuItem>
                  <MenuItem value="DELETED">{t('workspace.statusOptions.deleted')}</MenuItem>
                  <MenuItem value="ANONYMIZED">{t('workspace.statusOptions.anonymized')}</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>{t('workspace.profileLabel')}</InputLabel>
                <Select
                  value={profileFilter}
                  label={t('workspace.profileLabel')}
                  onChange={(e) => setProfileFilter(e.target.value as ProfileFilter)}
                >
                  <MenuItem value="all">{t('workspace.profileOptions.all')}</MenuItem>
                  <MenuItem value="COMPLETE">{t('workspace.profileOptions.complete')}</MenuItem>
                  <MenuItem value="PHONE_REQUIRED">
                    {t('workspace.profileOptions.phoneRequired')}
                  </MenuItem>
                </Select>
              </FormControl>
              <Button variant="outlined" onClick={onReset} startIcon={<ClearRoundedIcon />}>
                {t('common:action.reset')}
              </Button>
            </Stack>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
              useFlexGap
            >
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Chip
                  icon={<GroupsRoundedIcon />}
                  label={t('workspace.customerCount', { count: search.data?.totalElements ?? 0 })}
                />
                {search.data?.took != null ? (
                  <Chip
                    label={t('workspace.tookMs', { count: search.data.took })}
                    variant="outlined"
                  />
                ) : null}
              </Stack>
              {canManage ? (
                <Chip color="primary" variant="outlined" label={t('workspace.manageModeEnabled')} />
              ) : null}
            </Stack>
          </Stack>
        </Paper>

        {search.isLoading ? (
          <LoadingState label={t('workspace.loading')} />
        ) : search.isError ? (
          <ErrorState
            title={t('workspace.loadError.title')}
            message={search.error?.message ?? t('common:error.unknown')}
            onRetry={() => void search.refetch()}
          />
        ) : !search.data?.content.length ? (
          <EmptyState
            icon={<GroupsRoundedIcon sx={{ fontSize: 48 }} />}
            title={t('workspace.empty.title')}
            description={t('workspace.empty.description')}
            action={
              <Button variant="outlined" onClick={onReset} startIcon={<RefreshRoundedIcon />}>
                {t('workspace.empty.resetAction')}
              </Button>
            }
          />
        ) : (
          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2.5} alignItems="flex-start">
            <Paper variant="outlined" sx={{ flex: 1.2, width: '100%', overflow: 'hidden' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('workspace.table.customer')}</TableCell>
                    <TableCell>{t('workspace.table.contacts')}</TableCell>
                    <TableCell>{t('workspace.table.status')}</TableCell>
                    <TableCell>{t('workspace.table.channel')}</TableCell>
                    <TableCell>{t('workspace.table.updated')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {search.data.content.map((customer) => (
                    <CustomerRow
                      key={customer.customerId}
                      customer={customer}
                      selected={customer.customerId === selectedCustomer?.customerId}
                      onSelect={onSelectCustomer}
                    />
                  ))}
                </TableBody>
              </Table>
            </Paper>

            <Box sx={{ flex: 0.8, width: '100%', minWidth: { lg: 360 } }}>
              {selectedCustomer ? (
                <CustomerDetailPanel
                  customer={selectedCustomer}
                  canManageLoyalty={canManageLoyalty}
                />
              ) : (
                <EmptyState
                  title={t('workspace.noSelection.title')}
                  description={t('workspace.noSelection.description')}
                />
              )}
            </Box>
          </Stack>
        )}
      </Stack>
    </PageContainer>
  );
}
